import {
    ArrowDown,
    ArrowUp,
    ArrowUpDown,
    ChevronLeft,
    ChevronRight,
    Loader2,
} from 'lucide-react';
import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@apps/web/components/ui/button';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@apps/web/components/ui/table';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import {
    handleNavigableElementAuxClick,
    handleNavigableElementClick,
    type RowNavigation,
    shouldIgnoreNavigationEvent,
} from '@apps/web/lib/navigation_utils';
import {
    type AppColumnMeta,
    appTableFeatures,
    appTableFeaturesPreservingSelection,
    COLUMN_ALIGN_CLASS,
    type ColumnAlign,
    type ColumnDef,
    type ColumnDefTemplate,
    type ColumnFiltersState,
    type ColumnOrderState,
    type ColumnVisibilityState,
    flexRender,
    getPinnedLeftColumnIds,
    inMultiSelect,
    type OnChangeFn,
    type PaginationState,
    type RowData,
    type RowSelectionState,
    type SortingState,
    type Table as TableType,
    useTable,
} from '@apps/web/lib/table';
import { cn } from '@apps/web/lib/utils';

function useLocalStorageState<T>(
    key: string | undefined,
    initial: T,
    mergeStored?: (stored: T, initial: T) => T,
) {
    const readStorage = (k: string | undefined, fallback: T): T => {
        if (!k) {
            return fallback;
        }
        try {
            const raw = localStorage.getItem(k);
            if (!raw) {
                return fallback;
            }
            const stored = JSON.parse(raw) as T;
            return mergeStored ? mergeStored(stored, fallback) : stored;
        } catch {
            return fallback;
        }
    };

    const [value, setValue] = useState<T>(() => readStorage(key, initial));

    // Re-read from localStorage when the key changes
    const prevKeyRef = useRef(key);
    if (prevKeyRef.current !== key) {
        prevKeyRef.current = key;
        setValue(readStorage(key, initial));
    }

    useEffect(() => {
        if (!key) {
            return;
        }
        try {
            localStorage.setItem(key, JSON.stringify(value));
        } catch {
            // ignore quota errors
        }
    }, [key, value]);

    return [value, setValue] as const;
}

function mergeStoredColumnVisibility(
    stored: ColumnVisibilityState,
    initial: ColumnVisibilityState,
): ColumnVisibilityState {
    // A saved map predates columns added later. Apply defaults to those new
    // columns while preserving every visibility choice the user already made.
    return { ...initial, ...stored };
}

function prependFixedSorting(
    sorting: SortingState,
    fixedSorting: SortingState,
): SortingState {
    if (fixedSorting.length === 0) {
        return sorting;
    }
    const fixedColumnIds = new Set(fixedSorting.map(({ id }) => id));
    return [
        ...fixedSorting,
        ...sorting.filter(({ id }) => !fixedColumnIds.has(id)),
    ];
}

function removeFixedSorting(
    sorting: SortingState,
    fixedSorting: SortingState,
): SortingState {
    if (fixedSorting.length === 0) {
        return sorting;
    }
    const fixedColumnIds = new Set(fixedSorting.map(({ id }) => id));
    return sorting.filter(({ id }) => !fixedColumnIds.has(id));
}

/**
 * Renders a column's `header`/`cell` template without changing the React
 * element type when the column definitions are rebuilt.
 *
 * TanStack's `flexRender` renders a template function as a component *type*, so
 * a freshly-built column definition produces a brand-new type and React
 * unmounts and remounts every header and cell. Callers cannot avoid rebuilding:
 * the recommendations table's columns close over the agent's reasoning map and
 * suggested-caregiver set, which genuinely change while a run streams in.
 *
 * Those remounts crashed production (Sentry WEB-CS, 47 events / 28 users).
 * Remounting a Radix primitive re-runs its composed ref callback, which
 * setStates during the commit phase; the next render rebuilds the columns and
 * remounts everything again, and React aborts the chain with "Maximum update
 * depth exceeded" (React error #185) once it passes 50 nested commits.
 *
 * Going through this one component keeps the element type constant, so a column
 * rebuild re-renders headers and cells instead of remounting them. A template
 * is invoked rather than mounted, so one that uses hooks must use them
 * unconditionally — the same rule it already had as its own component.
 */
function ColumnTemplate<TProps extends object>({
    template,
    context,
}: {
    readonly template: ColumnDefTemplate<TProps> | undefined;
    readonly context: TProps;
}) {
    // Class and exotic (memo/forwardRef) components cannot be called directly,
    // and a non-function template is a plain node; both keep a stable type on
    // their own, so hand them back to flexRender.
    if (typeof template !== 'function' || isClassComponent(template)) {
        return flexRender(template, context);
    }
    return template(context);
}

function isClassComponent(template: (props: never) => unknown): boolean {
    return (
        (template as { prototype?: { isReactComponent?: unknown } }).prototype
            ?.isReactComponent !== undefined
    );
}

export type DataTableProps<TData extends RowData> = {
    readonly className?: string;
    readonly columns: Array<ColumnDef<TData>>;
    readonly data: Array<TData>;
    readonly onRowClick?: (row: TData, event: React.MouseEvent) => void;
    readonly onRowMouseEnter?: (row: TData) => void;
    readonly onRowMouseLeave?: (row: TData) => void;
    readonly onRowContextMenu?: (row: TData, event: React.MouseEvent) => void;
    /**
     * Return the navigation target for a row. When provided, DataTable owns
     * normal, Ctrl/Cmd-click, and middle-click navigation for that row.
     */
    readonly getRowNavigation?: (row: TData) => RowNavigation;
    /**
     * Restrict inline width styles to the listed column ids. When omitted,
     * inline widths are applied to every visible column.
     */
    readonly widthColumnIds?: ReadonlyArray<string>;

    // Optional features
    readonly enableRowSelection?: boolean;
    readonly rowSelection?: RowSelectionState;
    readonly onRowSelectionChange?: OnChangeFn<RowSelectionState>;

    readonly getRowClassName?: (row: TData) => string;
    readonly preserveSelectedRows?: boolean;
    readonly isLoading?: boolean;
    readonly loadingLabel?: string;
    readonly renderLoadingContent?: () => React.ReactNode;

    // Sorting + visibility
    readonly initialSorting?: SortingState;
    /**
     * Sort criteria that always precede the user's column sort. This is useful
     * for product-level grouping rules that column-header clicks must preserve.
     */
    readonly fixedSorting?: SortingState;
    readonly onSortingChange?: (sorting: SortingState) => void;
    readonly initialColumnVisibility?: ColumnVisibilityState;
    readonly initialColumnFilters?: ColumnFiltersState;
    /** Provide column filters to make this state fully controlled. */
    readonly columnFilters?: ColumnFiltersState;
    readonly columnVisibilityKey?: string; // persisted in localStorage
    readonly onColumnVisibilityChange?: OnChangeFn<ColumnVisibilityState>;
    readonly columnOrderKey?: string; // persisted in localStorage

    /** Persist global search + column filters.
     * We'll use `${filterKeyPrefix}:global` and `${filterKeyPrefix}:columns` */
    readonly filterKeyPrefix?: string;
    /** Callback when column filters change */
    readonly onColumnFiltersChange?: (filters: ColumnFiltersState) => void;
    /**
     * When true, column filters drive UI state only — row filtering is the
     * caller's responsibility (e.g. server-side). The rows passed in are
     * rendered as-is. Defaults to false (client-side filtering).
     */
    readonly manualFiltering?: boolean;

    readonly pageSize?: number;
    /** Provide pagination to make page index and page size fully controlled. */
    readonly pagination?: PaginationState;
    readonly onPaginationChange?: OnChangeFn<PaginationState>;
    /**
     * When true, data already represents one server page. DataTable renders
     * the rows as-is and uses `rowCount` to drive its pagination controls.
     */
    readonly manualPagination?: boolean;
    readonly rowCount?: number;
    /** Options for page size selector. If provided, a dropdown is shown to change page size. */
    readonly pageSizeOptions?: ReadonlyArray<number>;
    readonly onPageSizeChange?: (pageSize: number) => void;
    /** Hide the built-in pagination controls (useful when using server-side pagination) */
    readonly hidePagination?: boolean;
    /** Render pagination outside the rows or inside the rows scroll area. */
    readonly paginationPlacement?: 'outside' | 'rows';

    /** Optional ref for accessing the underlying TanStack table instance. */
    readonly tableRef?: React.MutableRefObject<TableType<TData> | null>;

    readonly renderToolbar?: (table: TableType<TData>) => React.ReactNode;
    readonly toolbarClassName?: string;
    readonly renderEmptyState?: () => React.ReactNode;
    /** Rendered beneath a row across all columns; null collapses it. */
    readonly renderExpandedRow?: (row: TData) => React.ReactNode;
    readonly renderRowsFooter?: (
        variant: 'mobile' | 'table',
    ) => React.ReactNode;
    /**
     * Class applied to the mobile card list wrapper. Use this for vertical
     * scroll caps while leaving the toolbar/search controls outside the
     * scrollable area.
     */
    readonly mobileRowsClassName?: string;
    /**
     * Class applied to the desktop table wrapper. Use this for vertical scroll
     * caps while leaving the toolbar/search controls outside the scrollable
     * area.
     */
    readonly tableRowsClassName?: string;
    /**
     * Optional render override used at viewports below `md` (768px). When
     * provided, the desktop table is replaced with a stacked list of these
     * cards on narrow screens, while toolbar + pagination keep their
     * positions. Falls back to the table on `md+`. When omitted, the
     * desktop table is shown but allowed to scroll horizontally on mobile.
     */
    readonly renderMobileCard?: (row: TData) => React.ReactNode;
    /** Optional column id to keep pinned to the right during horizontal scroll. */
    readonly stickyRightColumnId?: string;
    /** Add a fade gradient to the left edge of the sticky right column. */
    readonly stickyRightFade?: boolean;
    /**
     * Pin the column header row while the rows scroll. Only works when
     * `tableRowsClassName` makes the rows container the vertical scroller.
     */
    readonly stickyHeader?: boolean;
};

export function DataTable<TData extends RowData>({
    className,
    columns,
    data,
    onRowClick,
    onRowMouseEnter,
    onRowMouseLeave,
    onRowContextMenu,
    getRowNavigation,
    widthColumnIds,
    enableRowSelection = false,
    rowSelection: controlledRowSelection,
    onRowSelectionChange,
    getRowClassName,
    preserveSelectedRows = false,
    isLoading = false,
    loadingLabel = 'Loading…',
    renderLoadingContent,
    initialSorting,
    fixedSorting = [],
    onSortingChange: onSortingChangeProp,
    initialColumnVisibility = {},
    initialColumnFilters,
    columnFilters: controlledColumnFilters,
    columnVisibilityKey,
    onColumnVisibilityChange,
    columnOrderKey,
    filterKeyPrefix,
    onColumnFiltersChange: onColumnFiltersChangeProp,
    manualFiltering = false,
    renderToolbar,
    toolbarClassName,
    renderEmptyState,
    renderExpandedRow,
    renderRowsFooter,
    renderMobileCard,
    mobileRowsClassName,
    tableRowsClassName,
    pageSize: pageSizeProp,
    pagination: controlledPagination,
    onPaginationChange: onPaginationChangeProp,
    manualPagination = false,
    rowCount,
    pageSizeOptions,
    onPageSizeChange,
    hidePagination = false,
    paginationPlacement = 'outside',
    tableRef,
    stickyRightColumnId,
    stickyRightFade = false,
    stickyHeader = false,
}: DataTableProps<TData>) {
    const [sorting, setSorting] = useState<SortingState>(() =>
        removeFixedSorting(initialSorting ?? [], fixedSorting),
    );
    const effectiveSorting = React.useMemo(
        () => prependFixedSorting(sorting, fixedSorting),
        [fixedSorting, sorting],
    );
    const globalFilterKey = filterKeyPrefix
        ? `${filterKeyPrefix}:global`
        : undefined;
    const columnFiltersKey =
        controlledColumnFilters === undefined && filterKeyPrefix
            ? `${filterKeyPrefix}:columns`
            : undefined;
    const [globalFilter, setGlobalFilter] = useLocalStorageState<string>(
        globalFilterKey,
        '',
    );
    const [storedColumnFilters, setStoredColumnFilters] =
        useLocalStorageState<ColumnFiltersState>(
            columnFiltersKey,
            initialColumnFilters ?? [],
        );
    const columnFilters = controlledColumnFilters ?? storedColumnFilters;
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
    const [columnVisibility, setColumnVisibility] =
        useLocalStorageState<ColumnVisibilityState>(
            columnVisibilityKey,
            initialColumnVisibility,
            mergeStoredColumnVisibility,
        );
    const [columnOrder, setColumnOrder] =
        useLocalStorageState<ColumnOrderState>(columnOrderKey, []);
    const pageSize = controlledPagination?.pageSize ?? pageSizeProp ?? 1000;
    const isPaginationControlled = controlledPagination !== undefined;
    const isPageSizeControlled =
        isPaginationControlled ||
        (pageSizeProp !== undefined && onPageSizeChange !== undefined);
    const [internalPagination, setInternalPagination] =
        useState<PaginationState>({
            pageIndex: 0,
            pageSize,
        });
    const pagination = controlledPagination ?? internalPagination;
    const onPaginationChangeRef = useRef(onPaginationChangeProp);
    onPaginationChangeRef.current = onPaginationChangeProp;
    const setPagination = React.useCallback<OnChangeFn<PaginationState>>(
        (updater) => {
            if (!isPaginationControlled) {
                setInternalPagination(updater);
            }
            onPaginationChangeRef.current?.(updater);
        },
        [isPaginationControlled],
    );

    useEffect(() => {
        if (controlledPagination !== undefined || !isPageSizeControlled) {
            return;
        }
        if (pageSize !== internalPagination.pageSize) {
            setInternalPagination((prev) => ({
                ...prev,
                pageIndex: 0,
                pageSize,
            }));
        }
    }, [
        controlledPagination,
        internalPagination.pageSize,
        isPageSizeControlled,
        pageSize,
    ]);

    const pinnedLeftIds = useMemo(
        () => getPinnedLeftColumnIds(columns),
        [columns],
    );
    const effectiveColumnOrder = useMemo(
        () => [
            ...pinnedLeftIds,
            ...columnOrder.filter((id) => !pinnedLeftIds.includes(id)),
        ],
        [pinnedLeftIds, columnOrder],
    );

    // A stale stored hide would leave a pinned column with no UI to restore
    // it; the table's own initial defaults still apply.
    const effectiveColumnVisibility = useMemo(() => {
        const forcedIds = pinnedLeftIds.filter(
            (id) =>
                columnVisibility[id] === false &&
                initialColumnVisibility[id] !== false,
        );
        if (forcedIds.length === 0) {
            return columnVisibility;
        }
        const next = { ...columnVisibility };
        for (const id of forcedIds) {
            next[id] = true;
        }
        return next;
    }, [pinnedLeftIds, columnVisibility, initialColumnVisibility]);

    const table = useTable({
        features: preserveSelectedRows
            ? appTableFeaturesPreservingSelection
            : appTableFeatures,
        data,
        columns,
        state: {
            sorting: effectiveSorting,
            columnFilters,
            globalFilter,
            rowSelection: enableRowSelection
                ? (controlledRowSelection ?? rowSelection)
                : {},
            columnVisibility: effectiveColumnVisibility,
            columnOrder: effectiveColumnOrder,
            pagination,
        },
        enableRowSelection,
        manualFiltering,
        manualPagination,
        rowCount,
        // use the id so it routes through filterFns consistently
        globalFilterFn: 'containsText',
        onSortingChange: (updater) => {
            const updated =
                typeof updater === 'function'
                    ? updater(effectiveSorting)
                    : updater;
            const nextUserSorting = removeFixedSorting(updated, fixedSorting);
            setSorting(nextUserSorting);
            onSortingChangeProp?.(
                prependFixedSorting(nextUserSorting, fixedSorting),
            );
        },
        onColumnFiltersChange: (updater) => {
            const next =
                typeof updater === 'function'
                    ? updater(columnFilters)
                    : updater;
            if (controlledColumnFilters === undefined) {
                setStoredColumnFilters(next);
            }
            onColumnFiltersChangeProp?.(next);
        },
        onGlobalFilterChange: (updater) => {
            const next =
                typeof updater === 'function' ? updater(globalFilter) : updater;
            setGlobalFilter(next as string);
        },
        onRowSelectionChange: enableRowSelection
            ? (onRowSelectionChange ?? setRowSelection)
            : undefined,
        onColumnVisibilityChange: (updater) => {
            const next =
                typeof updater === 'function'
                    ? updater(columnVisibility)
                    : updater;
            setColumnVisibility(next);
            onColumnVisibilityChange?.(next);
        },
        onColumnOrderChange: (updater) => {
            const next =
                typeof updater === 'function' ? updater(columnOrder) : updater;
            setColumnOrder(next);
        },
        onPaginationChange: setPagination,
        autoResetPageIndex: false,
    });

    useEffect(() => {
        if (!tableRef) {
            return undefined;
        }
        tableRef.current = table;
        return () => {
            if (tableRef.current === table) {
                tableRef.current = null;
            }
        };
    }, [tableRef, table]);

    // On mount, drop restored filters for columns that no longer exist or can
    // no longer be filtered (e.g. a column whose facet was removed but whose
    // persisted localStorage filter would otherwise keep narrowing rows with no
    // UI to view or clear it), then notify the parent once of the sanitized
    // restored filters. Notifying with the sanitized set keeps consumers that
    // mirror column filters (e.g. cross-page select) from seeing a dropped one.
    const hasNotifiedParentRef = useRef(false);
    useEffect(() => {
        if (!columnFilters?.length) {
            return;
        }
        const filterableColumnIds = new Set(
            table
                .getAllLeafColumns()
                .filter((c) => c.getCanFilter())
                .map((c) => c.id),
        );
        const valid = columnFilters.filter((f) =>
            filterableColumnIds.has(f.id),
        );
        if (valid.length !== columnFilters.length) {
            if (controlledColumnFilters === undefined) {
                setStoredColumnFilters(valid);
            }
            hasNotifiedParentRef.current = true;
            onColumnFiltersChangeProp?.(valid);
            return;
        }
        if (
            !hasNotifiedParentRef.current &&
            valid.length > 0 &&
            onColumnFiltersChangeProp
        ) {
            hasNotifiedParentRef.current = true;
            onColumnFiltersChangeProp(valid);
        }
    }, [
        columns,
        table,
        columnFilters,
        controlledColumnFilters,
        setStoredColumnFilters,
        onColumnFiltersChangeProp,
    ]);

    // Reset to first page when filters change
    useEffect(() => {
        setPagination((prev) =>
            prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 },
        );
    }, [columnFilters, globalFilter, setPagination]);

    // Preserve the user's current page when data changes unless that page is
    // no longer valid after rows are added or removed.
    // Using data.length instead of the data reference avoids spurious updates
    // when SWR returns a new array reference with identical content.
    const prevDataLengthRef = useRef(data.length);
    useEffect(() => {
        if (manualPagination) {
            prevDataLengthRef.current = data.length;
            return;
        }
        if (data.length !== prevDataLengthRef.current) {
            prevDataLengthRef.current = data.length;
            setPagination((prev) => {
                const maxPageIndex = Math.max(
                    Math.ceil(data.length / prev.pageSize) - 1,
                    0,
                );
                if (prev.pageIndex <= maxPageIndex) {
                    return prev;
                }
                return {
                    ...prev,
                    pageIndex: maxPageIndex,
                };
            });
        }
    }, [data.length, manualPagination, setPagination]);

    const mobileRows = table.getRowModel().rows;
    const filteredRowCount = manualPagination
        ? (rowCount ?? data.length)
        : table.getFilteredRowModel().rows.length;
    const pageCount = Math.ceil(filteredRowCount / pagination.pageSize) || 1;
    const shouldRenderRowsPagination =
        !hidePagination && paginationPlacement === 'rows';
    const renderLoadingState = () =>
        renderLoadingContent ? (
            renderLoadingContent()
        ) : (
            <div className="flex items-center justify-center gap-2">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span className="text-muted-foreground">{loadingLabel}</span>
            </div>
        );
    const renderPaginationControls = (
        placement: 'outside' | 'rows',
    ): React.ReactNode => (
        <div
            className={cn(
                'bg-background flex items-center justify-between border-t px-2 py-2',
                placement === 'outside' && 'flex-shrink-0',
            )}
        >
            <div className="flex items-center gap-4">
                <div className="text-muted-foreground text-sm">
                    <span className="hidden @3xl:inline">
                        Showing{' '}
                        {Math.min(
                            pagination.pageIndex * pagination.pageSize + 1,
                            filteredRowCount,
                        )}{' '}
                        to{' '}
                        {Math.min(
                            (pagination.pageIndex + 1) * pagination.pageSize,
                            filteredRowCount,
                        )}{' '}
                        of {filteredRowCount} results
                    </span>
                    <span className="@3xl:hidden">
                        {filteredRowCount}{' '}
                        {filteredRowCount === 1 ? 'result' : 'results'}
                    </span>
                </div>
                {pageSizeOptions && pageSizeOptions.length > 0 && (
                    <div className="hidden items-center gap-2 @3xl:flex">
                        <span className="text-muted-foreground text-sm">
                            Rows per page:
                        </span>
                        <Select
                            value={pagination.pageSize.toString()}
                            onValueChange={(value) => {
                                const newPageSize = parseInt(value, 10);
                                if (
                                    controlledPagination === undefined &&
                                    isPageSizeControlled
                                ) {
                                    onPageSizeChange?.(newPageSize);
                                    return;
                                }
                                setPagination({
                                    pageIndex: 0,
                                    pageSize: newPageSize,
                                });
                                onPageSizeChange?.(newPageSize);
                            }}
                        >
                            <SelectTrigger size="sm" className="w-[90px]">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {pageSizeOptions.map((size) => (
                                    <SelectItem
                                        key={size}
                                        value={size.toString()}
                                    >
                                        {size}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                )}
            </div>
            <div className="flex items-center space-x-2">
                <Button
                    aria-label="Previous"
                    variant="outline"
                    size="sm"
                    className="px-2 md:px-3"
                    onClick={() =>
                        setPagination({
                            pageIndex: pagination.pageIndex - 1,
                            pageSize: pagination.pageSize,
                        })
                    }
                    disabled={pagination.pageIndex <= 0}
                >
                    <ChevronLeft className="h-4 w-4" />
                    <span className="hidden @3xl:inline">Previous</span>
                </Button>
                <div className="text-sm whitespace-nowrap">
                    Page {pagination.pageIndex + 1} of {pageCount}
                </div>
                <Button
                    aria-label="Next"
                    variant="outline"
                    size="sm"
                    className="px-2 md:px-3"
                    onClick={() =>
                        setPagination({
                            pageIndex: pagination.pageIndex + 1,
                            pageSize: pagination.pageSize,
                        })
                    }
                    disabled={pagination.pageIndex >= pageCount - 1}
                >
                    <span className="hidden @3xl:inline">Next</span>
                    <ChevronRight className="h-4 w-4" />
                </Button>
            </div>
        </div>
    );

    return (
        // Card/table layout switches on the viewport: desktop always gets the
        // table (scrolling horizontally when a side panel squeezes it) and
        // only phone-width viewports get the card list.
        <div className={cn('@container rounded-md border', className)}>
            {renderToolbar ? (
                <div className={cn('border-b p-2', toolbarClassName)}>
                    {renderToolbar(table as TableType<TData>)}
                </div>
            ) : null}
            {renderMobileCard ? (
                <div className={cn('md:hidden', mobileRowsClassName)}>
                    {isLoading ? (
                        <div className="p-6">{renderLoadingState()}</div>
                    ) : mobileRows.length > 0 ? (
                        <ul className="divide-y">
                            {mobileRows.map((row) => {
                                const original = row.original as TData;
                                return (
                                    <li
                                        key={row.id}
                                        className={cn(
                                            'active:bg-muted/40 cursor-pointer p-4',
                                            getRowClassName?.(original),
                                        )}
                                        onClick={(event) => {
                                            const navigation =
                                                getRowNavigation?.(original);
                                            if (navigation) {
                                                handleNavigableElementClick(
                                                    event,
                                                    navigation,
                                                );
                                                return;
                                            }
                                            if (
                                                shouldIgnoreNavigationEvent(
                                                    event,
                                                )
                                            ) {
                                                return;
                                            }
                                            onRowClick?.(original, event);
                                        }}
                                        onAuxClick={
                                            getRowNavigation
                                                ? (event) =>
                                                      handleNavigableElementAuxClick(
                                                          event,
                                                          getRowNavigation(
                                                              original,
                                                          ),
                                                      )
                                                : undefined
                                        }
                                    >
                                        {renderMobileCard(original)}
                                    </li>
                                );
                            })}
                        </ul>
                    ) : renderEmptyState ? (
                        <div className="p-4">{renderEmptyState()}</div>
                    ) : (
                        <div className="text-muted-foreground p-6 text-center">
                            No results.
                        </div>
                    )}
                    {renderRowsFooter?.('mobile')}
                    {shouldRenderRowsPagination
                        ? renderPaginationControls('rows')
                        : null}
                </div>
            ) : null}
            <div
                className={cn(
                    'overflow-x-auto',
                    renderMobileCard ? 'hidden md:block' : '',
                    tableRowsClassName,
                )}
            >
                <Table containerClassName="overflow-visible">
                    <TableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id} className="group">
                                {headerGroup.headers.map((header) => {
                                    const isStickyRightColumn =
                                        stickyRightColumnId !== undefined &&
                                        header.column.id ===
                                            stickyRightColumnId;
                                    const width = widthColumnIds?.includes(
                                        header.column.id,
                                    )
                                        ? header.getSize()
                                        : undefined;
                                    return (
                                        <TableHead
                                            key={header.id}
                                            aria-sort={
                                                header.column.getIsSorted() ===
                                                'asc'
                                                    ? 'ascending'
                                                    : header.column.getIsSorted() ===
                                                        'desc'
                                                      ? 'descending'
                                                      : undefined
                                            }
                                            className={cn(
                                                stickyHeader &&
                                                    'bg-background sticky top-0 z-20',
                                                isStickyRightColumn &&
                                                    'bg-background group-hover:bg-muted sticky right-0 z-30 overflow-visible',
                                                isStickyRightColumn &&
                                                    stickyRightFade &&
                                                    'before:from-background group-hover:before:from-muted before:pointer-events-none before:absolute before:inset-y-0 before:-left-8 before:w-8 before:bg-gradient-to-l before:to-transparent',
                                            )}
                                            style={{
                                                width,
                                                maxWidth:
                                                    header.column.columnDef
                                                        .maxSize,
                                                minWidth:
                                                    header.column.columnDef
                                                        .minSize,
                                            }}
                                        >
                                            {header.isPlaceholder ? null : (
                                                <ColumnTemplate
                                                    template={
                                                        header.column.columnDef
                                                            .header
                                                    }
                                                    context={header.getContext()}
                                                />
                                            )}
                                        </TableHead>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell
                                    colSpan={columns.length}
                                    className="h-24 text-center"
                                >
                                    {renderLoadingState()}
                                </TableCell>
                            </TableRow>
                        ) : table.getRowModel().rows?.length ? (
                            table.getRowModel().rows.map((row) => {
                                // Check if this selected row only
                                // appears because preserveSelectedRows
                                // bypassed the filters.
                                let isPreservedBySelection = false;
                                if (
                                    preserveSelectedRows &&
                                    row.getIsSelected() &&
                                    columnFilters.length > 0
                                ) {
                                    isPreservedBySelection = columnFilters.some(
                                        (cf) =>
                                            !inMultiSelect(
                                                row,
                                                cf.id,
                                                cf.value,
                                            ),
                                    );
                                }
                                const tableRow = (
                                    <TableRow
                                        key={row.id}
                                        data-state={
                                            row.getIsSelected() && 'selected'
                                        }
                                        className={cn(
                                            stickyRightColumnId !== undefined
                                                ? 'hover:bg-muted group cursor-pointer'
                                                : 'hover:bg-muted/50 group cursor-pointer',
                                            isPreservedBySelection &&
                                                'bg-muted/30 opacity-60',
                                            getRowClassName?.(
                                                row.original as TData,
                                            ),
                                        )}
                                        onClick={(event) => {
                                            const original =
                                                row.original as TData;
                                            const navigation =
                                                getRowNavigation?.(original);
                                            if (navigation) {
                                                handleNavigableElementClick(
                                                    event,
                                                    navigation,
                                                );
                                                return;
                                            }
                                            if (
                                                shouldIgnoreNavigationEvent(
                                                    event,
                                                )
                                            ) {
                                                return;
                                            }
                                            onRowClick?.(original, event);
                                        }}
                                        onAuxClick={
                                            getRowNavigation
                                                ? (event) =>
                                                      handleNavigableElementAuxClick(
                                                          event,
                                                          getRowNavigation(
                                                              row.original as TData,
                                                          ),
                                                      )
                                                : undefined
                                        }
                                        onContextMenu={(event) => {
                                            onRowContextMenu?.(
                                                row.original as TData,
                                                event,
                                            );
                                        }}
                                        onMouseEnter={() => {
                                            onRowMouseEnter?.(
                                                row.original as TData,
                                            );
                                        }}
                                        onMouseLeave={() => {
                                            onRowMouseLeave?.(
                                                row.original as TData,
                                            );
                                        }}
                                    >
                                        {row.getVisibleCells().map((cell) => {
                                            const isStickyRightColumn =
                                                stickyRightColumnId !==
                                                    undefined &&
                                                cell.column.id ===
                                                    stickyRightColumnId;
                                            const cellMeta = cell.column
                                                .columnDef.meta as
                                                | AppColumnMeta
                                                | undefined;
                                            const width =
                                                widthColumnIds?.includes(
                                                    cell.column.id,
                                                )
                                                    ? cell.column.getSize()
                                                    : undefined;
                                            return (
                                                <TableCell
                                                    key={cell.id}
                                                    className={cn(
                                                        'overflow-x-hidden',
                                                        COLUMN_ALIGN_CLASS[
                                                            cellMeta?.align ??
                                                                'left'
                                                        ],
                                                        cellMeta?.cellClassName,
                                                        isStickyRightColumn &&
                                                            'bg-background group-hover:bg-muted sticky right-0 z-20 overflow-visible',
                                                        isStickyRightColumn &&
                                                            stickyRightFade &&
                                                            'before:from-background group-hover:before:from-muted before:pointer-events-none before:absolute before:inset-y-0 before:-left-8 before:w-8 before:bg-gradient-to-l before:to-transparent',
                                                    )}
                                                    style={{
                                                        width,
                                                        maxWidth:
                                                            cell.column
                                                                .columnDef
                                                                .maxSize,
                                                        minWidth:
                                                            cell.column
                                                                .columnDef
                                                                .minSize,
                                                    }}
                                                >
                                                    <ColumnTemplate
                                                        template={
                                                            cell.column
                                                                .columnDef.cell
                                                        }
                                                        context={cell.getContext()}
                                                    />
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                );
                                const expandedContent = renderExpandedRow?.(
                                    row.original as TData,
                                );
                                const expandedRow = expandedContent ? (
                                    <TableRow
                                        key={`${row.id}-expanded`}
                                        className="hover:bg-transparent"
                                    >
                                        <TableCell
                                            colSpan={
                                                row.getVisibleCells().length
                                            }
                                            className="pt-0"
                                        >
                                            {expandedContent}
                                        </TableCell>
                                    </TableRow>
                                ) : null;
                                if (isPreservedBySelection) {
                                    return (
                                        <React.Fragment key={row.id}>
                                            <Tooltip>
                                                <TooltipTrigger asChild={true}>
                                                    {tableRow}
                                                </TooltipTrigger>
                                                <TooltipContent
                                                    side="top"
                                                    align="center"
                                                    avoidCollisions={false}
                                                >
                                                    Visible because
                                                    pre-selected. Does not match
                                                    active filters.
                                                </TooltipContent>
                                            </Tooltip>
                                            {expandedRow}
                                        </React.Fragment>
                                    );
                                }
                                if (expandedRow) {
                                    return (
                                        <React.Fragment key={row.id}>
                                            {tableRow}
                                            {expandedRow}
                                        </React.Fragment>
                                    );
                                }
                                return tableRow;
                            })
                        ) : renderEmptyState ? (
                            <TableRow>
                                <TableCell colSpan={columns.length}>
                                    {renderEmptyState()}
                                </TableCell>
                            </TableRow>
                        ) : (
                            <TableRow>
                                <TableCell
                                    colSpan={columns.length}
                                    className="h-24 text-center"
                                >
                                    No results.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                {renderRowsFooter?.('table')}
                {shouldRenderRowsPagination
                    ? renderPaginationControls('rows')
                    : null}
            </div>
            {!hidePagination && paginationPlacement === 'outside'
                ? renderPaginationControls('outside')
                : null}
        </div>
    );
}

export function DataTableColumnHeader<TData extends RowData, TValue>({
    column,
    title,
    className,
    titleAdornment,
    tooltip,
}: {
    column: {
        getCanSort: () => boolean;
        getIsSorted: () => false | 'asc' | 'desc';
        toggleSorting: (desc?: boolean) => void;
        columnDef?: { meta?: unknown };
    };
    title: string;
    className?: string;
    titleAdornment?: React.ReactNode;
    tooltip?: string;
}) {
    const sortDirection = column.getIsSorted();
    const align: ColumnAlign =
        (column.columnDef?.meta as AppColumnMeta | undefined)?.align ?? 'left';
    const justifyClass = cn(
        COLUMN_ALIGN_CLASS[align],
        align === 'right'
            ? 'justify-end'
            : align === 'center'
              ? 'justify-center'
              : 'justify-start',
    );
    const SortIcon =
        sortDirection === 'asc'
            ? ArrowUp
            : sortDirection === 'desc'
              ? ArrowDown
              : ArrowUpDown;
    // Unsorted columns reveal their glyph on hover/focus; only the active
    // sort stays visible, so the header row reads as labels, not controls.
    const sortIcon = (
        <span
            className={cn(
                'flex items-center',
                align === 'right' ? 'mr-2' : 'ml-2',
                !sortDirection &&
                    'text-muted-foreground opacity-0 transition-opacity group-hover/sort:opacity-100 group-focus-visible/sort:opacity-100',
            )}
        >
            <SortIcon aria-hidden={true} className="h-4 w-4" />
        </span>
    );
    const sortButton = (
        <Button
            variant="ghost"
            className={cn(
                'group/sort flex h-10 items-center px-0',
                justifyClass,
                !titleAdornment && 'w-full',
                !titleAdornment && className,
            )}
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
            {align === 'right' && sortIcon}
            {title}
            {align !== 'right' && sortIcon}
        </Button>
    );
    const content = !column.getCanSort() ? (
        <div className={cn('flex h-10 items-center', justifyClass, className)}>
            {title}
            {titleAdornment}
        </div>
    ) : titleAdornment ? (
        <div
            className={cn(
                'flex h-10 w-full items-center',
                justifyClass,
                className,
            )}
        >
            {sortButton}
            {titleAdornment}
        </div>
    ) : (
        sortButton
    );

    if (!tooltip) {
        return content;
    }

    return (
        <Tooltip>
            <TooltipTrigger asChild={true}>{content}</TooltipTrigger>
            <TooltipContent>{tooltip}</TooltipContent>
        </Tooltip>
    );
}

// Re-export types for consumers
export type { ColumnFiltersState } from '@apps/web/lib/table';
